import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { useSearchParams } from "react-router";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import { refreshUser } from "@app/utils/use-most-fresh-user";
import { readAuthPolicy } from "@app/utils/auth-policy";
import PscLoginButton from "@app/components/PscLoginButton";

interface StatusResponse {
  available?: boolean;
  environment?: string;
}

interface UnlinkResponse {
  linked?: boolean;
  detail?: string;
  error?: string;
}

/**
 * CPS / e-CPS card — linking and unlinking.
 *
 * Linking happens **from an open session**, and it is the only way a card comes to
 * open an account: a card login resolves the link, never an RPPS found on the account.
 * Once the card is linked, the RPPS the account carries is the one the ANS vouches for
 * rather than the one somebody typed.
 *
 * Password + TOTP come first, and the server refuses a link without them: linked
 * alone, the card would be the account's only way in, and a wallet outage would lock
 * its holder out.
 *
 * The card only appears when Pro Santé Connect is actually wired up on this
 * instance: the flag alone is not enough, the client credentials have to be there
 * too. Until the ANS grants them, this block is invisible.
 */
export default function PscSettings() {
  const { t } = useLingui();
  const user = useUser((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [available, setAvailable] = useState(false);
  const [environment, setEnvironment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const policy = readAuthPolicy(user);
  const linked = Boolean(policy?.psc_linked);
  // A doctor cannot unlink: the server refuses it, since one click would undo the
  // obligation. Changing card is done by linking the new one, which replaces it.
  const required = Boolean(policy?.psc_required);
  const missing = policy?.missing_factors ?? [];
  const twoFactorsReady =
    !missing.includes("password") && !missing.includes("totp");
  const identifiers = user as {
    rpps?: string | null;
    adeli?: string | null;
  } | null;
  const rpps = identifiers?.rpps ?? null;
  const adeli = identifiers?.adeli ?? null;

  useEffect(() => {
    API.get({ path: "/user/psc/status" }).then((res: StatusResponse) => {
      setAvailable(Boolean(res?.available));
      setEnvironment(res?.environment ?? "");
    });
  }, []);

  // The ANS sends the browser back through a redirect, not through a fetch, so the
  // outcome arrives in the URL. Read it once, then strip it, so a page refresh does
  // not replay the message.
  useEffect(() => {
    const failure = searchParams.get("psc_error");
    const success = searchParams.get("psc_linked");
    if (!failure && !success) return;
    if (failure) {
      setError(
        failure === "card_already_linked"
          ? t`Cette carte est déjà associée à un autre compte MAGE-X.`
          : failure === "factors_missing"
            ? t`Définissez d'abord un mot de passe et activez la double authentification : ils restent votre accès si Pro Santé Connect est indisponible.`
            : failure === "cancelled"
              ? t`Association annulée.`
              : t`L'association a échoué. Réessayez, ou contactez l'équipe MAGE-X.`,
      );
    } else {
      void refreshUser("PscSettings");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("psc_error");
    next.delete("psc_linked");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  function unlink() {
    setBusy(true);
    setError("");
    API.post({ path: "/user/psc/unlink" })
      .then(async (res: UnlinkResponse) => {
        if (res?.linked === false) {
          await refreshUser("PscSettings");
        } else {
          setError(
            res?.detail || res?.error || t`Impossible de dissocier la carte.`,
          );
        }
      })
      .finally(() => setBusy(false));
  }

  if (!available) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Carte CPS (Pro Santé Connect)</Trans>
        </CardTitle>
        <CardDescription>
          {required ? (
            <Trans>
              Obligatoire pour les médecins. Une fois votre carte CPS ou votre
              e-CPS associée, vous vous connectez en un clic, sans mot de passe
              ni code ; ceux-ci restent votre accès de secours si Pro Santé
              Connect est indisponible.
            </Trans>
          ) : (
            <Trans>
              Associez votre carte CPS ou votre e-CPS pour vous connecter en un
              clic, sans mot de passe ni code. C'est le moyen de connexion
              attendu des professionnels de santé porteurs d'un RPPS.
            </Trans>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {environment === "bas" && (
          <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <Trans>
              Cette instance est raccordée au Bac à Sable de l'ANS : seule une
              e-CPS de test fonctionne ici.
            </Trans>
          </p>
        )}

        {linked ? (
          <>
            <p className="text-sm font-medium text-primary">
              <Trans>✓ Votre carte est associée à ce compte.</Trans>
            </p>
            {rpps && (
              <p className="text-sm text-slate-600">
                <Trans>RPPS : {rpps}</Trans>
              </p>
            )}
            {adeli && (
              <p className="text-sm text-slate-600">
                <Trans>ADELI : {adeli}</Trans>
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            {required ? (
              <PscLoginButton
                intent="link"
                hint={
                  <p className="mt-1 text-center text-xs text-slate-500">
                    <Trans>
                      Pour changer de carte, associez la nouvelle : elle
                      remplace celle-ci.
                    </Trans>
                  </p>
                }
              />
            ) : (
              <>
                <Button variant="outline" onClick={unlink} disabled={busy}>
                  {busy ? (
                    <Trans>Dissociation...</Trans>
                  ) : (
                    <Trans>Dissocier ma carte</Trans>
                  )}
                </Button>
                <p className="text-xs text-slate-500">
                  <Trans>
                    La dissociation exige que le compte garde un autre moyen de
                    connexion : un mot de passe et la double authentification.
                  </Trans>
                </p>
              </>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              <Trans>Aucune carte n'est associée à ce compte.</Trans>
            </p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {/* The official button here too: linking IS an identification through Pro Santé
                Connect, and a second, home-made button for the same act is what the charter
                refuses. The sentence goes underneath, never beside it. Until the account holds
                password + TOTP there is no button at all: a greyed-out copy of the official one
                would be that home-made button. */}
            {twoFactorsReady ? (
              <PscLoginButton
                intent="link"
                hint={
                  <p className="mt-1 text-center text-xs text-slate-500">
                    <Trans>
                      Vous serez redirigé vers Pro Santé Connect, puis ramené
                      ici.
                    </Trans>
                  </p>
                }
              />
            ) : (
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <Trans>
                  Définissez d'abord un mot de passe et activez la double
                  authentification (blocs ci-dessus) : ils restent votre accès
                  si Pro Santé Connect est indisponible. Vous pourrez ensuite
                  associer votre carte.
                </Trans>
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
